import { type ProductData } from "../../../models/ProductData";
import { PopperTooltip } from "../Tooltip";
import { Fragment } from "react";
import { uniqueValues } from "../../../utils/misc";

export interface AliasesProps {
  product: ProductData;
  mode?: GetAliasesMode;
  packCode?: string;
  visibleAliasesLayout?: VisibleAliasesLayout;
  maxAliasesVisible?: number;
}

export type GetAliasesMode = "all" | "15-digits" | "13-digits";
type VisibleAliasesLayout = "inline" | "separate-rows";

export const ProductAliases = ({
  product,
  mode = "all",
  visibleAliasesLayout = "separate-rows",
  packCode,
  maxAliasesVisible = 2,
}: AliasesProps) => {
  let allAliases = getAliases(product, mode, packCode);
  allAliases = uniqueValues(allAliases);

  if (allAliases.length > maxAliasesVisible) {
    const visibleAliases = allAliases.slice(0, maxAliasesVisible);
    return (
      <PopperTooltip
        tooltipContent={
          <div>
            {allAliases.map((alias, index) => {
              const lastEl = index === allAliases.length - 1;
              return (
                <Fragment key={alias}>
                  <span>{alias}</span>
                  {!lastEl && <br />}
                </Fragment>
              );
            })}
          </div>
        }
      >
        {visibleAliasesLayout === "inline" && <span>{visibleAliases.join(", ")},...</span>}
        {visibleAliasesLayout === "separate-rows" && (
          <div>
            {visibleAliases.map((alias) => (
              <Fragment key={alias}>
                <span>{alias}</span>
                <br />
              </Fragment>
            ))}
            <span>...</span>
          </div>
        )}
      </PopperTooltip>
    );
  }

  if (visibleAliasesLayout === "inline") {
    return <span>{allAliases.join(", ")}</span>;
  }
  if (visibleAliasesLayout === "separate-rows") {
    return (
      <div>
        {allAliases.map((alias, index) => {
          const lastEl = index === allAliases.length - 1;
          return (
            <Fragment key={alias}>
              <span>{alias}</span>
              {!lastEl && (
                <>
                  {","}
                  <br />
                </>
              )}
            </Fragment>
          );
        })}
      </div>
    );
  }

  throw new Error("unhandled case");
};

export const getAliases = (product: ProductData, mode: GetAliasesMode, packCode?: string) => {
  switch (mode) {
    case "all": {
      let aliases = product.aliases;
      if (packCode) {
        aliases = aliases.concat(product.packageAliases[packCode] ?? []);
      } else {
        aliases = aliases.concat(Object.values(product.packageAliases).flat());
      }
      return aliases;
    }
    case "15-digits": {
      return packCode ? product.packageAliases[packCode] || [] : [];
    }
    case "13-digits": {
      return product.aliases;
    }
  }
  return [];
};
